import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { breadcrumbList, faqPage, jsonLdScript } from '@/lib/schema'
import { clampDescription, firstThatFits } from '@/lib/meta'

export interface GlossaryPageData {
  term: string
  slug: string
  definition: string
  whyItMatters: string
  howUptrackHelps: string
  relatedTerms: { name: string; href: string }[]
  formula?: string
}

export function glossaryHead(data: GlossaryPageData) {
  const title = firstThatFits([
    `${data.term} — What It Is & Why It Matters | Uptrack`,
    `${data.term} — Definition | Uptrack`,
    `${data.term} | Uptrack`,
  ])
  const description = clampDescription(data.definition.replace(/\n/g, ' '))

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: 'https://uptrack.app/og/glossary-term.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: `https://uptrack.app/glossary/${data.slug}` },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Glossary', url: 'https://uptrack.app/glossary' },
        { name: data.term, url: `https://uptrack.app/glossary/${data.slug}` },
      ])),
      jsonLdScript(faqPage([
        { question: `What is ${data.term}?`, answer: data.definition },
        { question: `Why does ${data.term} matter?`, answer: data.whyItMatters },
        { question: `How does Uptrack help with ${data.term}?`, answer: data.howUptrackHelps },
      ])),
    ],
  }
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section className="border-t border-border">
      <div className="container mx-auto px-4 py-12 md:py-16">{children}</div>
    </section>
  )
}

function Paragraphs({ text }: { text: string }) {
  return (
    <>
      {text.split('\n\n').map((p, i) => (
        <p key={i} className="mb-4 max-w-2xl text-base leading-relaxed text-body last:mb-0">
          {p}
        </p>
      ))}
    </>
  )
}

export function GlossaryPage({ data }: { data: GlossaryPageData }) {
  return (
    <>
        {/* Hero */}
        <section>
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h1 className="max-w-3xl text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              What is {data.term}?
            </h1>
          </div>
        </section>

        {/* Definition */}
        <Section>
          <div className="grid gap-6 lg:grid-cols-12">
            <h2 className="text-[1.5rem] font-semibold leading-tight tracking-tight lg:col-span-4">Definition</h2>
            <div className="lg:col-span-8">
              <Paragraphs text={data.definition} />
            </div>
          </div>
        </Section>

        {/* Formula (optional) */}
        {data.formula && (
          <Section>
            <div className="grid gap-6 lg:grid-cols-12">
              <h2 className="text-[1.5rem] font-semibold leading-tight tracking-tight lg:col-span-4">Formula</h2>
              <div className="lg:col-span-8">
                <div className="max-w-2xl rounded-lg border border-border bg-card px-4 py-3">
                  <code className="font-mono text-lg font-semibold text-foreground">{data.formula}</code>
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Why it matters */}
        <Section>
          <div className="grid gap-6 lg:grid-cols-12">
            <h2 className="text-[1.5rem] font-semibold leading-tight tracking-tight lg:col-span-4">Why it matters</h2>
            <div className="lg:col-span-8">
              <Paragraphs text={data.whyItMatters} />
            </div>
          </div>
        </Section>

        {/* How Uptrack helps */}
        <Section>
          <div className="grid gap-6 lg:grid-cols-12">
            <h2 className="text-[1.5rem] font-semibold leading-tight tracking-tight lg:col-span-4">How Uptrack helps</h2>
            <div className="lg:col-span-8">
              <Paragraphs text={data.howUptrackHelps} />
              <div className="mt-8">
                <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                  <Link to="/pricing">
                    See plans
                    <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </Section>

        {/* Related terms */}
        <Section>
          <div className="grid gap-6 lg:grid-cols-12">
            <h2 className="text-[1.5rem] font-semibold leading-tight tracking-tight lg:col-span-4">
              Related terms
            </h2>
            <ul className="flex flex-wrap gap-2 lg:col-span-8">
              {data.relatedTerms.map((t) => (
                <li key={t.href}>
                  <Link
                    to={t.href}
                    className="inline-block rounded-md border border-border bg-card px-3.5 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary"
                  >
                    {t.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* CTA */}
        <Section>
          <div className="grid items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Start monitoring your sites now</h2>
              <p className="mt-3 max-w-lg text-lead text-body">
                Start free, or choose a plan, for 30-second monitoring and alert confirmation.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </Section>
    </>
  )
}
